import gsap from "gsap";
import PixelReveal from "./pixel-reveal";
import { pageReady } from "../../utils/page-ready";
import { loadVidstack } from "../../utils/vidstack";
import { observeMediaElement } from "../../utils/mediaTelemetry";
import { loadBgVideo, initBgVideoOnView } from "../../utils/bgVideo";
import { BP } from "../../utils/breakpoints";

// Card intro (see animateIn). The pixel reveal starts REVEAL_DELAY seconds after the
// intro starts; the intro ends at INTRO_DURATION. Starting the reveal before the end
// means the media shows while the card is still (very slightly) scaled, which can
// bring back the hairline in Chrome/Safari: 0.8 (= INTRO_DURATION) is the safe value.
const INTRO_DURATION = 0.8;
const REVEAL_DELAY = 0.2;

export default class CollectionCard {
  constructor(el) {
    this.el = el;

    this.animateIn();

    if (el.querySelector("media-player")) loadVidstack();

    el.querySelectorAll(".c-collection-card__media").forEach((cell) => {
      cell.querySelectorAll("media-player, video").forEach((player) => {
        const isHover = player.classList.contains(
          "c-collection-card__media__layer--hover",
        );
        observeMediaElement(player, {
          context: "collection-card",
          kind: isHover ? "hover" : "base",
          src: player.getAttribute("src") || player.dataset.src,
        });

        // Base layer autoplays once it's near the viewport; the hover layer
        // (below) is only ever loaded on demand, never ahead of time.
        // Through the PlaybackBudget: only the nearest ones play at once on
        // tablet/mobile (four videos per card).
        if (!isHover && player.tagName !== "MEDIA-PLAYER") {
          initBgVideoOnView(player, { budget: true });
        }
      });

      const hover = cell.querySelector(".c-collection-card__media__layer--hover");
      if (!hover || !["MEDIA-PLAYER", "VIDEO"].includes(hover.tagName)) return;
      const isPlayer = hover.tagName === "MEDIA-PLAYER";

      cell.addEventListener("mouseenter", () => {
        const attempt = () => {
          loadBgVideo(hover);
          const p = hover.play?.();
          if (p && typeof p.catch === "function") p.catch(() => {});
        };
        if (isPlayer) loadVidstack().then(attempt);
        else attempt();
      });
      cell.addEventListener("mouseleave", () => hover.pause?.());
    });
  }

  // Fade in + slight translate + scale the first time the card enters the viewport.
  // The media stays hidden under its pixel cover while the card scales, and the reveal
  // only starts once the card is (nearly) at rest: a scaled card is rendered off the
  // pixel grid in Chrome/Safari, so a visible media would show a hairline at its edges. Nothing is
  // left behind after the tween for the same reason (see onComplete).
  animateIn() {
    // Translate only on tablet/mobile: scaling a card full of videos re-rasters
    // it every frame of the intro, which the phones feel.
    const scale = window.matchMedia(BP.tabletAndMobile).matches ? 1 : 0.95;
    gsap.set(this.el, { y: "20%", opacity: 0, scale });
    PixelReveal.claim(this.el);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        pageReady.ready.then(() => {
          gsap.to(this.el, {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: INTRO_DURATION,
            ease: "expo.out",
            onComplete: () => gsap.set(this.el, { clearProps: "transform,opacity" }),
          });
          gsap.delayedCall(REVEAL_DELAY, () => {
            // Same 0.06s step as the --zoom-delay hover stagger.
            this.el.querySelectorAll("[data-pixel-reveal]").forEach((m, i) => PixelReveal.play(m, i * 0.06));
          });
        });
      },
      { threshold: 0, rootMargin: "0px 0px -20% 0px" }
    );

    observer.observe(this.el);
  }
}
